import { init, MountElement, paymentIntent } from '@nemuru-eng/checkout-js';
import {
  getLocaleByPurchaseCountry,
  PaymentMethodType,
  WorkflowLogChannel,
  WorkflowPlace,
} from '@nemuru-eng/js-api-client';

import { useCheckoutSessionContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/contexts/CheckoutSessionContext';
import { usePaymentIntent } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/hooks/usePaymentIntent';
import { CreditCardPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/CreditCard/_shared/entities/PaymentMethodDetail/CreditCardPaymentMethodDetailEntity';
import { ENV_ENV } from '@/config/envVars';
import { useUserContext } from '@/contexts/UserContext/UserContext';
import { isIbcStandsAgentUser, isIbcStandsSupervisorUser } from '@/hooks/layoutConstructor/iberiaUserHelpers';
import { useMutation } from '@/hooks/useMutation';

export const useMountCreditCardPaymentIntentMutation = ({
  domSelector,
  paymentMethodDetailEntity,
}: {
  domSelector: string;
  paymentMethodDetailEntity: CreditCardPaymentMethodDetailEntity;
}) => {
  const { checkoutSessionEntity } = useCheckoutSessionContext();
  const { refreshPaymentMethodEntity } = usePaymentIntent();
  const { userEntity } = useUserContext();

  const unmountCreditCardPaymentIntent = async (pt: MountElement) => {
    pt.unmount();
    mutation.reset();
    await refreshPaymentMethodEntity(paymentMethodDetailEntity);
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (!checkoutSessionEntity || !userEntity) {
        return;
      }

      const agentId = checkoutSessionEntity.agentId;
      const env = ENV_ENV;

      const clientSecret = await checkoutSessionEntity.getClientSecret();
      const locale = getLocaleByPurchaseCountry(checkoutSessionEntity.checkoutSession.purchaseCountry);

      const isStandsAgent = isIbcStandsAgentUser(userEntity);
      const isStandsSupervisor = isIbcStandsSupervisorUser(userEntity);
      const isStandsAgentOrSupervisor = isStandsAgent || isStandsSupervisor;

      await init(agentId, { env });

      const pt = paymentIntent({
        clientSecret,
        paymentMethodType: PaymentMethodType.CREDIT_CARD,
        options: {
          locale,
        },
        onStepSucceded: async (stepName) => {
          // Stands agents and supervisors can complete the whole flow, so the widget stays open for both.
          if (isStandsAgentOrSupervisor) {
            return;
          }

          if (stepName === WorkflowPlace.GDPR) {
            await unmountCreditCardPaymentIntent(pt);
          }
        },
        closeButton: {
          enabled: true,
          onClick: async () => {
            await unmountCreditCardPaymentIntent(pt);
          },
        },
        userId: userEntity.user.id,
        /**
         * TODO:
         * Review if we should allow other users other than telemarketing and stands
         *  to continue operations from the admin.
         *
         *  If that's the case should we attribute the worflow step to the right channel?
         *    And if they're not suppossed to do that, should we limit the UI? Is anybody relying on that? like somebody with merchant creds?
         *
         *  Added a comment about all this to be investigated/handled in the following ticket: IBCT-142
         */
        channel: isStandsAgentOrSupervisor ? WorkflowLogChannel.STANDS : WorkflowLogChannel.TELEMARKETING,
      });

      pt.mount(domSelector);
    },
  });

  return mutation;
};
